import { useState, useEffect, useCallback } from "react";
import { Check, Search, Plus, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";

interface TagOption {
  id: string;
  name: string;
  color: string;
}

interface AutomationTagSelectorProps {
  workspaceId: string;
  selectedTagId?: string;
  selectedTagName?: string;
  onSelect: (tag: TagOption) => void;
}

export function AutomationTagSelector({
  workspaceId,
  selectedTagId,
  selectedTagName,
  onSelect,
}: AutomationTagSelectorProps) {
  const [tags, setTags] = useState<TagOption[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const fetchTags = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    const { data } = await supabase
      .from("lead_tags")
      .select("id, name, color")
      .eq("workspace_id", workspaceId)
      .order("name");
    setTags(data || []);
    setLoading(false);
  }, [workspaceId]);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  const filtered = tags.filter((t) =>
    t.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleCreateTag = async () => {
    if (!search.trim() || !workspaceId) return;
    setCreating(true);
    try {
      const { data, error } = await supabase
        .from("lead_tags")
        .insert({ name: search.trim(), color: "bg-gray-500", workspace_id: workspaceId })
        .select("id, name, color")
        .single();

      if (error) {
        if (error.code === "23505") {
          // Duplicate — find the existing one
          const existing = tags.find(
            (t) => t.name.toLowerCase() === search.trim().toLowerCase()
          );
          if (existing) onSelect(existing);
        }
        return;
      }

      if (data) {
        onSelect(data);
        setSearch("");
        await fetchTags();
      }
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-2">
        <div className="h-9 bg-muted rounded-md animate-pulse" />
        <div className="h-20 bg-muted rounded-md animate-pulse" />
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {selectedTagId && selectedTagName && (
        <div className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-primary/10 text-primary text-sm">
          <Check className="w-4 h-4" />
          <span>{selectedTagName}</span>
        </div>
      )}

      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Buscar tag..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-8 h-9"
        />
      </div>

      <div className="space-y-0.5 max-h-40 overflow-y-auto">
        {filtered.length === 0 && search.trim() ? (
          <div className="space-y-1 px-2 py-1">
            <p className="text-sm text-muted-foreground">Nenhuma tag encontrada</p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="w-full justify-start gap-2"
              disabled={creating}
              onClick={handleCreateTag}
            >
              {creating ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Plus className="w-3.5 h-3.5" />
              )}
              Criar tag "{search.trim()}"
            </Button>
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground px-2 py-1">
            Nenhuma tag cadastrada
          </p>
        ) : null}
        {filtered.map((tag) => {
          const isSelected = tag.id === selectedTagId;
          return (
            <button
              key={tag.id}
              type="button"
              onClick={() => onSelect(tag)}
              className={cn(
                "w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-colors",
                isSelected
                  ? "bg-primary/10 text-primary"
                  : "hover:bg-muted"
              )}
            >
              <div className={cn("w-3 h-3 rounded-full flex-shrink-0", tag.color)} />
              <span className="flex-1 text-left truncate">{tag.name}</span>
              {isSelected && <Check className="w-4 h-4 flex-shrink-0" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}
